import { type FC, memo, useEffect, useState } from "react";

import Carousel from "@/design-system/carousel";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import useAjax from "@Components/Hooks/useAjax";
import { Loading } from "@Components/Loading";
import { type TelemetryClickFromType, urlFormat } from "@Components/Modules/Helpers";
import Text from "@Components/Text";
import { Brand } from "@Routes/BrandLinks";
import * as stylex from "@stylexjs/stylex";

import { type QuerySmMax, type QueryXsMax, colors } from "../../styles/tokens.stylex";
import type { ILogoTile } from "../common/LogoTile";
import LogoTile from "../common/LogoTile";

const smMax: QuerySmMax = "@media (max-width: 989px)";
const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    suggestedFlyersCarousel: {
        gridAutoColumns: "1fr",
    },

    container: {
        padding: { default: "0 30px", [smMax]: "0 -10px" },
    },

    gridContainer: {
        gap: "20px",
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
    },

    suggestedFlyersTitle: {
        textTransform: "none",
        marginBottom: "20px",
    },

    logo: {
        borderColor: colors.b_centas,
        borderStyle: "solid",
        borderWidth: "1px",
        aspectRatio: "1/1",
        width: { default: "inherit", [xsMax]: "142px" },
    },
    arrowStyle: {
        color: colors.fg_Primary,
    },
});

export interface ISuggestedFlyers {
    brandId: string;
    clickFrom: TelemetryClickFromType;
    itemsToShow?: number;
}

export const SuggestedFlyers: FC<ISuggestedFlyers> = memo((props) => {
    const title = languageSelector(
        "Popularne gazetki z najnowszymi promocjami",
        "Popular flyers with latest promotions",
    );

    const [showFlyers, setShowFlyers] = useState(true);
    const { isMobile } = useViewportInfoContext();

    const { isLoading, data, request } = useAjax<ILogoTile[]>({
        ajaxAction: urlFormat(
            Brand,
            {
                action: "MostPopularFlyers",
                brandId: props.brandId,
                origin: props.clickFrom,
            },
            true,
        ),
        initialData: [],
        success: (response) => {
            if (response?.length === 0) {
                setShowFlyers(false);
            }
        },
    });

    useEffect(request, []);

    const renderBody = () => {
        if (isLoading) {
            return <Loading />;
        }

        if (isMobile) {
            return (
                <Carousel
                    styleX={styles.suggestedFlyersCarousel}
                    carouselContainerStyleX={styles.container}
                    arrowStyleX={styles.arrowStyle}
                    centerMode={false}
                    fullWidth
                    showArrows
                >
                    {data.slice(0, props.itemsToShow || 5).map((value) => (
                        <LogoTile key={value.id} {...value} styleX={styles.logo} />
                    ))}
                </Carousel>
            );
        }

        return (
            <div {...stylex.props(styles.gridContainer)}>
                {data.slice(0, props.itemsToShow || 5).map((value) => (
                    <LogoTile key={value.id} {...value} styleX={styles.logo} />
                ))}
            </div>
        );
    };

    return (
        showFlyers && (
            <div>
                <header>
                    <Text fontStyle="h4" textElement="h4" styleX={styles.suggestedFlyersTitle}>
                        {title}
                    </Text>
                </header>
                {renderBody()}
            </div>
        )
    );
});
